import { Injectable } from '@angular/core';
import { Observable, shareReplay, Subject, timeout } from 'rxjs';
import { Notification, Ticket } from '../models';
import { PusherService } from './pusher.service';
import { TicketService } from './ticket.service';

interface CachedTicketRequest {
  stream: Observable<any>;
  /** Instante (ms) até o qual a resposta pode ser reaproveitada por outro canal. */
  expiresAt: number;
}

@Injectable()
export class NotificationService {
  /**
   * Janela em que a resposta de um bilhete é compartilhada entre as entregas do
   * MESMO evento. O backend emite cada evento em `ticket-{id}` e em
   * `serviceConfig-{id}`; sem a dedup eram dois GETs idênticos por evento.
   * A chave inclui o evento cru, então eventos distintos nunca são coalescidos.
   */
  private static readonly DEDUP_WINDOW_MS = 250;

  /**
   * Teto para um GET pendurado. Sem ele o cache em voo (`expiresAt = Infinity`)
   * nunca é liberado: um backend/proxy que segure a conexão sem responder
   * imobiliza aquela chave — nenhum GET novo e nenhuma notificação — até o
   * reload do app. No timeout o stream emite erro e o bilhete é liberado.
   */
  private static readonly REQUEST_TIMEOUT_MS = 10000;

  private notifications$ = new Subject<Notification>();

  /** Cache por `{ticketId}:{evento cru}` para coalescer a dupla entrega. */
  private ticketRequests = new Map<string, CachedTicketRequest>();

  /**
   * Última versão COMPLETA conhecida de cada bilhete (só entra via GET).
   * O payload do Pusher não traz 5 campos (`timezoneId`, `locationName`,
   * `providerName`, `smartCode`, `activities`) — o fast path mescla o payload
   * sobre esta base para nunca publicar null onde já havia dado.
   */
  private lastKnownTickets = new Map<number, Ticket>();

  notifications = this.notifications$.asObservable();

  /**
   * Repassa o sinal de reconexão do Pusher. As telas usam para ressincronizar:
   * eventos publicados durante a queda não são reentregues.
   */
  get reconnections(): Observable<void> {
    return this.pusherService.reconnections;
  }

  constructor(
    private ticketService: TicketService,
    private pusherService: PusherService
  ) { }

  sendNotification(notification: Notification): void {
    this.notifications$.next(notification);
  }

  add(ticketId: number, serviceConfigId: number, eventNames: string[]): void {
    const events = new Map<string, Function>();
    const serviceConfigEvent = new Map<string, Function>();
    serviceConfigEvent.set('TicketStart', (data) => this.parseMessage(data, 'ServiceConfigTicketStart', 'TicketStart'));
    serviceConfigEvent.set('TicketCalled', (data) => this.parseMessage(data, 'ServiceConfigTicketCalled', 'TicketCalled'));
    serviceConfigEvent.set('TicketCancel', (data) => this.parseMessage(data, 'ServiceConfigTicketCancel', 'TicketCancel'));
    serviceConfigEvent.set('TicketCheckin', (data) => this.parseMessage(data, 'ServiceConfigTicketCheckin', 'TicketCheckin'));

    for (const eventName of eventNames) {
      events.set(eventName, (data) => this.parseMessage(data, eventName));
    }
    this.pusherService.add(`ticket-${ticketId}`, events);
    this.pusherService.add(`serviceConfig-${serviceConfigId}`, serviceConfigEvent);
  }

  /**
   * @param rawEventName nome do evento como o Pusher entrega. É a chave de
   * dedup: o mesmo evento chega em dois canais com o mesmo nome cru, mas com
   * `eventName` (rótulo consumido pelos componentes) diferente.
   */
  parseMessage(data: any, eventName: string, rawEventName: string = eventName): void {
    const id = data?.ticket?.id ?? data?.TicketId;

    // Evento sem payload válido não pode derrubar o handler.
    if (id === null || id === undefined) return;

    // Fast path (F1.3): update imediato a partir do payload, mesclado sobre a
    // última versão completa — nunca introduz null. Só em cache-miss: em hit a
    // resposta compartilhada já chega de imediato via replay.
    // O GET de reconciliação continua (é o backup para eventos perdidos em
    // queda de conexão — o Pusher não reentrega).
    const key = `${id}:${rawEventName}`;
    const cached = this.ticketRequests.get(key);
    const isHit = !!cached && Date.now() < cached.expiresAt;
    let fast: Notification | null = null;
    if (!isHit) {
      fast = this.buildFastNotification(data, eventName);
      if (fast) this.sendNotification(fast);
    }

    this.fetchTicketOnce(id, rawEventName).subscribe({
      next: (response) => {
        const notification = this.buildNotification(response, eventName);
        this.lastKnownTickets.set(id, notification.ticket);
        // O fast já entregou este evento com dados iguais: o 2º send só
        // duplicaria efeitos colaterais (som, modal, badge) nos consumidores.
        if (fast && this.ticketsEqual(fast.ticket, notification.ticket)) return;
        this.sendNotification(notification);
      },
      error: (error) => console.error(`[NotificationService] falha ao buscar o bilhete ${id}`, error)
    });
  }

  /**
   * Um GET por bilhete+evento dentro da janela de dedup. Em voo, a request é
   * sempre compartilhada; após resolver, vale por `DEDUP_WINDOW_MS`. Em caso de
   * erro o bilhete é liberado na hora, permitindo nova tentativa.
   */
  private fetchTicketOnce(id: number, rawEventName: string): Observable<any> {
    const key = `${id}:${rawEventName}`;
    const cached = this.ticketRequests.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.stream;
    }

    this.pruneExpired();

    const stream = this.ticketService.getTicket(id).pipe(
      timeout(NotificationService.REQUEST_TIMEOUT_MS),
      shareReplay(1)
    );
    const entry: CachedTicketRequest = { stream, expiresAt: Number.POSITIVE_INFINITY };
    this.ticketRequests.set(key, entry);

    // Assinatura de aquecimento: dispara o GET e marca quando a resposta chega.
    stream.subscribe({
      next: () => { entry.expiresAt = Date.now() + NotificationService.DEDUP_WINDOW_MS; },
      error: () => { entry.expiresAt = 0; }
    });

    return stream;
  }

  /**
   * Descarta entradas vencidas ao inserir uma nova. Sem isso o cache cresceria
   * indefinidamente num totem 24/7 — e cada entrada retém o último payload do
   * bilhete (`shareReplay(1)` guarda o valor). Entradas em voo (`expiresAt`
   * infinito) nunca são podadas.
   */
  private pruneExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.ticketRequests) {
      if (now >= entry.expiresAt) {
        this.ticketRequests.delete(key);
      }
    }
  }

  /**
   * Fast path (F1.3): monta uma notificação imediata a partir do payload do
   * Pusher, mesclada sobre a última versão COMPLETA conhecida.
   *
   * Retorna null quando não há base (primeiro evento daquele bilhete): nesse
   * caso só o GET fala, para nunca publicar null onde já havia dado. Os 5
   * campos que o payload não traz (`timezoneId`, `locationName`,
   * `providerName`, `smartCode`, `activities`) são herdados da base.
   */
  private buildFastNotification(data: any, eventName: string): Notification | null {
    const id = data?.ticket?.id ?? data?.TicketId;
    if (id === null || id === undefined) return null;
    const base = this.lastKnownTickets.get(id);
    if (!base) return null;

    const t = data?.ticket ?? {};
    const resourceName = t.resource?.name ?? (typeof t.resource === 'string' ? t.resource : undefined);
    return {
      eventName,
      ticket: {
        ...base,
        id,
        prevision: t.prevision ?? data?.EstimatedServiceStart ?? base.prevision,
        status: t.status ?? base.status,
        resource: resourceName ?? base.resource,
        attendanceType: t.attendanceType ?? base.attendanceType,
        providerId: data?.ProviderId ?? base.providerId,
        serviceConfigId: data?.ServiceConfigId ?? base.serviceConfigId,
        friendlyCode: t.friendlyCode ?? base.friendlyCode,
        accessKey: t.accessKey ?? base.accessKey,
        locationId: data?.LocationId ?? base.locationId,
        serviceName: t.service?.name ?? base.serviceName,
        feedback: data?.Feedback ?? base.feedback,
      },
      message: "Chegou uma notificação"
    };
  }

  /**
   * Compara o ticket do fast com o da reconciliação. `position` fica de fora:
   * é sempre null nos dois (posição é resolvida à parte, via `getNewPosition`).
   * Objetos/listas vão por JSON — a ordem das chaves é a mesma porque os dois
   * lados são construídos pelos builders deste service.
   */
  private ticketsEqual(a: Ticket, b: Ticket): boolean {
    return a.id === b.id
      && a.prevision === b.prevision
      && a.status === b.status
      && a.resource === b.resource
      && a.attendanceType === b.attendanceType
      && a.timezoneId === b.timezoneId
      && a.providerId === b.providerId
      && a.serviceConfigId === b.serviceConfigId
      && a.friendlyCode === b.friendlyCode
      && a.accessKey === b.accessKey
      && a.locationId === b.locationId
      && a.locationName === b.locationName
      && a.providerName === b.providerName
      && a.serviceName === b.serviceName
      && JSON.stringify(a.feedback ?? null) === JSON.stringify(b.feedback ?? null)
      && JSON.stringify(a.smartCode ?? null) === JSON.stringify(b.smartCode ?? null)
      && JSON.stringify(a.activities ?? []) === JSON.stringify(b.activities ?? []);
  }

  private buildNotification(response: any, eventName: string): Notification {
    return {
      eventName,
      ticket: {
        id: response.id,
        prevision: response.estimatedServiceStart,
        status: response.status,
        resource: response.resource ? response.resource.name : null,
        attendanceType: response.attendanceType,
        position: null,
        timezoneId: response.location.timezoneId,
        providerId: response.provider.id,
        serviceConfigId: response.session.serviceConfigId,
        friendlyCode: response.friendlyCode,
        accessKey: response.accessKey,
        locationId: response.location.id,
        locationName: response.location.name,
        providerName: response.provider.name,
        serviceName: response.service.name,
        feedback: response.feedback,
        smartCode: response.smartCode,
        activities: response.activities || []
      },
      message: "Chegou uma notificação"
    };
  }

  remove(ticketId: number, serviceConfigId: number): void {
    for (const key of Array.from(this.ticketRequests.keys())) {
      if (key.startsWith(`${ticketId}:`)) {
        this.ticketRequests.delete(key);
      }
    }
    this.lastKnownTickets.delete(ticketId);
    this.pusherService.remove(`ticket-${ticketId}`);
    this.pusherService.remove(`serviceConfig-${serviceConfigId}`);
  }
}
